import Phaser from 'phaser';
import { getDominoBackTextureKey } from '../config/dominoAssets';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin } from '../config/viewerCosmetics';
import { getViewerBackTextureKey } from '../config/viewerTileSkinAssets';

/**
 * Screen-space targets for the four player card areas.
 * Relative to the local player: self=bottom, right/top/left = opponents.
 */
export interface DealTargets {
  self:  { x: number; y: number };
  right: { x: number; y: number };
  top:   { x: number; y: number };
  left:  { x: number; y: number };
}

// ── Timing constants ──────────────────────────────────────────────────────────
const TILES_PER_PLAYER  = 7;
const SCATTER_STAGGER   = 18;   // ms between each fake tile appearing
const SCATTER_FADE_MS   = 160;  // fade-in duration per tile
const SCATTER_SETTLE_MS = 150;  // pause after last tile appears before dealing
const DEAL_STAGGER      = 45;   // ms between tiles dealt to same player
const DEAL_TRAVEL_MS    = 340;  // travel duration per dealt tile

/**
 * Visual-only shuffle/deal animation shown at the start of each new game.
 *
 * Phase A — scatter: 28 fake domino backs appear in a loose pile near the
 * table centre, staggered by SCATTER_STAGGER ms each.
 *
 * Phase B — deal: each fake back travels outward toward one of the four
 * player areas, staggered within each direction, then fades away.  Real
 * game state is unaffected and rendered underneath throughout.
 *
 * Call destroy() at any time to abort and clean up (scene shutdown, etc.).
 */
export class ShuffleDealAnimator {
  private scene:      Phaser.Scene;
  private fakes:      Phaser.GameObjects.Image[] = [];
  private done =      false;
  private _onComplete: () => void;
  // Cosmetics: viewer-selected tile skin (durable). Current selection
  // source is temporary — see config/viewerCosmetics.ts.
  private viewerTileSkin: ViewerTileSkin;

  constructor(
    scene:      Phaser.Scene,
    /** Board/table centre in scene coordinates. */
    cx:         number,
    cy:         number,
    /** Display size (px) for each fake tile — match board tile dimensions. */
    tileW:      number,
    tileH:      number,
    targets:    DealTargets,
    onComplete: () => void,
    viewerTileSkin: ViewerTileSkin = 'classic',
  ) {
    this.scene       = scene;
    this._onComplete = onComplete;
    this.viewerTileSkin = viewerTileSkin;
    this._run(cx, cy, tileW, tileH, targets);
  }

  /** Resolves the back texture for this viewer's skin (durable), falling
   *  back to classic if that skin's back texture failed to load. */
  private resolveBackTextureKey(): string {
    const key = getViewerBackTextureKey(this.viewerTileSkin);
    return this.scene.textures.exists(key) ? key : getDominoBackTextureKey();
  }

  /** Abort immediately — kills all tweens, destroys all fake sprites. Safe to call twice. */
  destroy(): void {
    if (this.done) return;
    this.done = true;
    for (const img of this.fakes) {
      if (img?.scene) {
        this.scene.tweens.killTweensOf(img);
        img.destroy();
      }
    }
    this.fakes = [];
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private _run(
    cx:      number,
    cy:      number,
    tileW:   number,
    tileH:   number,
    targets: DealTargets,
  ): void {
    const total = TILES_PER_PLAYER * 4;

    // ── Phase A: scatter fake backs near centre ───────────────────────────
    for (let i = 0; i < total; i++) {
      const ox  = (Math.random() - 0.5) * 120;   // ±60 px
      const oy  = (Math.random() - 0.5) * 80;    // ±40 px
      const rot = (Math.random() - 0.5) * 0.42;  // ±~12°

      const img = this.scene.add.image(cx + ox, cy + oy, this.resolveBackTextureKey());
      img.setDisplaySize(tileW, tileH);
      img.setRotation(rot);
      img.setAlpha(0);
      img.setDepth(195);
      this.fakes.push(img);

      this.scene.tweens.add({
        targets:  img,
        alpha:    0.88,
        duration: SCATTER_FADE_MS,
        delay:    i * SCATTER_STAGGER,
        ease:     'Sine.easeOut',
      });
    }

    // ── Phase B: deal to four player areas ───────────────────────────────
    // All four directions deal in parallel; tiles within each direction are
    // staggered by DEAL_STAGGER ms.
    const phaseADone = total * SCATTER_STAGGER + SCATTER_FADE_MS + SCATTER_SETTLE_MS;

    const dirs: Array<{ x: number; y: number }> = [
      targets.self,
      targets.right,
      targets.top,
      targets.left,
    ];

    // One counter for all 28 deal tweens; when it reaches zero we finish.
    let pending = total;
    const onOneTweenDone = () => {
      pending--;
      if (pending <= 0) this._finish();
    };

    for (let p = 0; p < 4; p++) {
      const target = dirs[p];
      for (let t = 0; t < TILES_PER_PLAYER; t++) {
        const fake  = this.fakes[p * TILES_PER_PLAYER + t];
        const delay = phaseADone + t * DEAL_STAGGER;

        this.scene.tweens.add({
          targets:  fake,
          x:        target.x,
          y:        target.y,
          rotation: 0,
          alpha:    0,
          duration: DEAL_TRAVEL_MS,
          delay,
          ease:     'Power2.easeOut',
          onComplete: onOneTweenDone,
        });
      }
    }
  }

  private _finish(): void {
    if (this.done) return;
    this.done = true;
    for (const img of this.fakes) {
      if (img?.scene) img.destroy();
    }
    this.fakes = [];
    this._onComplete();
  }
}
